type ProblemAction = {
  text?: string;
  url?: string;
};

type LegacyAppError = {
  actionInfo?: string;
  appActionTitle?: string;
  appActionMessage?: string;
  appActionButtonTitle?: string;
  originalException?: {
    response?: {
      error?: LegacyAppError;
    };
  };
};

export type EnhancedErrorResponse = {
  title?: string;
  detail?: string;
  action?: ProblemAction;
  response?: {
    error?: LegacyAppError;
  };
};

export type EnhancedError = Error & {
  originalError?: unknown;
  status?: number;
  errorResponse?: EnhancedErrorResponse;
  title?: string;
  buttonText?: string;
  buttonUrl?: string;
};

type ErrorDetails = {
  userMessage: string;
  fullError: unknown;
  status: number | undefined;
  errorResponse: EnhancedErrorResponse;
};

export const handleErrors = (error: any, throwError: boolean = true) => {
  const isEnhancedError = error?.originalError && error?.status && error?.errorResponse;

  const status = isEnhancedError ? error.status : error?.response?.status || error?.status || error?.statusCode;
  const errorResponseData = isEnhancedError ? error.errorResponse : error?.response?.data || error;

  const originalExceptionError = errorResponseData?.response?.error?.originalException?.response?.error;
  const isBlocklistError = originalExceptionError?.actionInfo === 'Borrower Blocklist';

  const errorMessage = isBlocklistError
    ? originalExceptionError?.appActionMessage
    : errorResponseData?.response?.error?.appActionMessage || errorResponseData?.response?.error || errorResponseData?.detail;

  const defaultMessage =
    'Thank you for applying with Credit Union. Our Home Equity products are currently not available in your state. If you are interested in exploring other Credit Union products. Please return to see our other offerings.';

  let userMessage: string;

  switch (status) {
    case 400:
    case 401:
    case 403:
      console.error(error);
      userMessage =
        errorMessage || 'Thank you for applying. Unfortunately, we were not able to pre-qualify you, but we will contact you to discuss your application within 1-2 business days.';
      break;
    case 404:
      console.error(error);
      userMessage = defaultMessage;
      break;
    case 500:
      console.error(error);
      // For 500 errors, check if it's a wrapped blocklist error
      userMessage = errorMessage || 'Internal server error';
      break;
    default:
      if (errorMessage) {
        userMessage = errorMessage;
      } else {
        userMessage = 'An unexpected error occurred.';
      }
  }

  if (throwError) {
    throw new Error(userMessage);
  }

  return {
    userMessage,
    fullError: error,
    status,
    errorResponse: errorResponseData,
  };
};

/**
 * Helper function to create enhanced error objects from handleErrors results
 * This eliminates code duplication across service functions
 */
export const createEnhancedError = (detailsError: ErrorDetails): EnhancedError =>
  Object.assign(new Error(detailsError.userMessage), {
    originalError: detailsError.fullError,
    status: detailsError.status,
    errorResponse: detailsError.errorResponse,
    title: detailsError.errorResponse?.title,
    buttonText: detailsError.errorResponse?.action?.text,
    buttonUrl: detailsError.errorResponse?.action?.url,
  });

/**
 * Helper function to extract error properties from error objects
 * Prioritizes blocklist errors nested in originalException (for 500 errors wrapping 403 blocklist errors)
 * This ensures blocklist error messages are displayed correctly
 */
export const getErrorProperty = (error: unknown, property: 'appActionTitle' | 'appActionMessage' | 'appActionButtonTitle'): string | undefined => {
  const errorResponse = (error as any)?.errorResponse?.response?.error;
  const originalExceptionError = errorResponse?.originalException?.response?.error;

  // Check nested originalException path FIRST (for blocklist errors wrapped in 500)
  if (originalExceptionError?.actionInfo === 'Borrower Blocklist' && originalExceptionError?.[property]) {
    return originalExceptionError[property];
  }

  // Check nested originalException path even if not blocklist (for other nested errors)
  if (originalExceptionError?.[property]) {
    return originalExceptionError[property];
  }

  // Fall back to top-level error values
  const enhancedValue = errorResponse?.[property];
  if (enhancedValue) {
    return enhancedValue;
  }

  const directValue = (error as any)?.response?.error?.[property];
  if (directValue) {
    return directValue;
  }

  const nestedValue = (error as any)?.response?.data?.response?.error?.[property];
  if (nestedValue) {
    return nestedValue;
  }

  return undefined;
};
